// src/components/auth/Login.jsx
import React, { useState, useContext } from 'react';
import {
    Container, Box, Typography, TextField, Button,
    Paper, Avatar, CircularProgress, Alert, Link, IconButton
} from '@mui/material';
import { LockOutlined, Security as SecurityIcon, Home as HomeIcon } from '@mui/icons-material';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { AuthContext } from '../../contexts/AuthContext';

const validationSchema = Yup.object({
    email: Yup.string()
        .email('Enter a valid email')
        .required('Email is required'),
    password: Yup.string()
        .required('Password is required')
});

const Login = () => {
    const [error, setError] = useState(null);
    const { login, loading } = useContext(AuthContext);
    const navigate = useNavigate();

    const formik = useFormik({
        initialValues: {
            email: '',
            password: ''
        },
        validationSchema: validationSchema,
        onSubmit: async (values) => {
            try {
                setError(null);
                await login(values);
                navigate('/dashboard');
            } catch (err) {
                setError(err.message || 'Failed to login. Please check your credentials.');
            }
        }
    });

    return (
        <Box
            sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                minHeight: { xs: '100vh', sm: 'calc(100vh - 128px)' },
                width: '100%',
                px: { xs: 2, sm: 0 },
                py: { xs: 2, sm: 0 }
            }}
        >
            <Container maxWidth="xs">
                <Paper
                    elevation={3}
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        p: { xs: 3, sm: 4 },
                        borderRadius: 2,
                        position: 'relative'
                    }}
                >
                    {/* Home Button */}
                    <IconButton
                        component={RouterLink}
                        to="/"
                        sx={{
                            position: 'absolute',
                            top: { xs: 8, sm: 16 },
                            left: { xs: 8, sm: 16 },
                            color: 'text.secondary',
                            '&:hover': {
                                color: 'primary.main',
                                bgcolor: 'primary.light',
                            }
                        }}
                        size="small"
                        aria-label="Go to home page"
                    >
                        <HomeIcon />
                    </IconButton>

                    {/* Logo */}
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 3, mt: { xs: 2, sm: 0 } }}>
                        <SecurityIcon sx={{ fontSize: 32, color: 'primary.main', mr: 1 }} />
                        <Typography variant="h4" component="div" color="primary.main" fontWeight="bold">
                            RiskAS
                        </Typography>
                    </Box>

                    <Avatar sx={{ m: 1, bgcolor: 'primary.main' }}>
                        <LockOutlined />
                    </Avatar>

                    <Typography component="h1" variant="h5">
                        Sign in
                    </Typography>

                    {error && (
                        <Alert severity="error" sx={{ width: '100%', mt: 2 }}>
                            {error}
                        </Alert>
                    )}

                    <Box component="form" onSubmit={formik.handleSubmit} sx={{ mt: 1, width: '100%' }}>
                        <TextField
                            margin="normal"
                            required
                            fullWidth
                            id="email"
                            label="Email Address"
                            name="email"
                            autoComplete="email"
                            autoFocus
                            value={formik.values.email}
                            onChange={formik.handleChange}
                            error={formik.touched.email && Boolean(formik.errors.email)}
                            helperText={formik.touched.email && formik.errors.email}
                        />

                        <TextField
                            margin="normal"
                            required
                            fullWidth
                            name="password"
                            label="Password"
                            type="password"
                            id="password"
                            autoComplete="current-password"
                            value={formik.values.password}
                            onChange={formik.handleChange}
                            error={formik.touched.password && Boolean(formik.errors.password)}
                            helperText={formik.touched.password && formik.errors.password}
                        />

                        <Button
                            type="submit"
                            fullWidth
                            variant="contained"
                            sx={{ mt: 3, mb: 2, py: 1.2 }}
                            disabled={loading}
                            startIcon={loading ? <CircularProgress size={20} /> : null}
                        >
                            {loading ? 'Signing in...' : 'Sign In'}
                        </Button>

                        <Box sx={{
                            display: 'flex',
                            flexDirection: { xs: 'column', sm: 'row' },
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            gap: { xs: 1, sm: 0 },
                            mt: 2
                        }}>
                            <Link component={RouterLink} to="/register" variant="body2">
                                {"Don't have an account? Sign Up"}
                            </Link>
                            <Link
                                component={RouterLink}
                                to="/"
                                variant="body2"
                                sx={{
                                    color: 'text.secondary',
                                    '&:hover': { color: 'primary.main' }
                                }}
                            >
                                Back to Home
                            </Link>
                        </Box>
                    </Box>
                </Paper>
            </Container>
        </Box>
    );
};

export default Login;